/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\pages\Devis\ConfigurateurArticle\components\DetailPieces.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

/**
SELLERIE MARINE - COMPOSANT DÉTAIL DES PIÈCES

📋 DESCRIPTION:
Composant React de génération et affichage détaillé des pièces de découpe.
Calcul automatique des fournitures nécessaires avec fermetures adaptatives.

🎯 RESPONSABILITÉS:
- Génération automatique du détail des pièces par article
- Calcul des fournitures nécessaires (mousses, fermetures, accessoires)
- Gestion des fermetures adaptatives avec sélection automatique
- Support spécialisé pour bâches (œillets, renforts, sangles, assemblage)
- Interface de validation pour l'atelier avec dimensions précises
- Calcul des coûts fournitures avec prix adaptatifs

🔗 DÉPENDANCES:
- types.ts: Interfaces Article et Matiere
- ApiService: Génération détail pièces via backend
- MousseService: Catalogue mousses avec prix au m³
- FermetureService: Fermetures adaptatives intelligentes
- React: Hooks useState, useEffect pour gestion d'état

📡 INTERACTIONS:
- Backend: Génération pièces via API /api/devis/pieces-detail
- Services: Calcul fournitures avec prix adaptatifs
- Parent: Communication nombre pièces calculées
- Atelier: Export détaillé pour découpe et montage

📊 STATUT: ACTIF (Production)
📅 Dernière mise à jour: Juin 2025
👤 Responsable: Équipe développement

💡 NOTES:
- Fermetures adaptatives avec sélection automatique selon stock
- Support complet bâches (œillets, renforts, sangles, assemblage)
- Calcul mousses avec nouveau système prix au m³
- Interface atelier avec dimensions précises et alertes
- Gestion fallback pour compatibilité ancien système
*/
import React, { useState, useEffect } from 'react';
import { Article, Matiere } from '../types';
import { apiService } from '../../../../services/ApiService';
import { mousseService } from '../../../../services/MousseService';
import { fermetureService } from '../../../../services/FermetureService';

interface DetailPiecesProps {
  articles: Article[];
  matieres: Matiere[];
  devisId?: string;
  devisReference?: string;
  onPiecesCalculated?: (nombrePieces: number) => void;
  devisOptimise?: any; // Données du calepinage optimisé
  optimisationActive?: boolean; // Indique si l'optimisation est active
  onClose?: () => void; // Callback appelé à la fermeture
  onDevisOptimiseUpdate?: (newDevisOptimise: any) => void; // Callback pour mettre à jour devisOptimise dans le parent
}

interface PieceDetail {
  article: string;
  piece: string;
  dimensions: string;
  surface: number;
  matiere: string;
  couleur: string;
}

interface FournitureDetail {
  article: string;
  type: string;
  dimensions?: string;
  volume?: number;
  quantite: number;
  unite: string;
  prixUnitaire: number;
}

interface EstimationDetails {
  longueur_area_cm: number;
  k_applique: number;
  longueur_estimee_cm: number;
  longueur_min_layout_cm: number;
  longueur_finale_cm: number;
}

interface GroupeMatiereDetail {
  matiere: string;
  matiereNom: string;
  couleur: string;
  laize: number;
  longueurTotale: number;
  tauxChute?: number;
  optimise: boolean;
  estimationDetails?: EstimationDetails;
}

const DetailPieces: React.FC<DetailPiecesProps> = ({ 
  articles, 
  matieres, 
  devisId, 
  devisReference, 
  onPiecesCalculated,
  devisOptimise,
  optimisationActive = false,
  onClose,
  onDevisOptimiseUpdate
}) => {
  const [pieces, setPieces] = useState<PieceDetail[]>([]);
  const [fournitures, setFournitures] = useState<FournitureDetail[]>([]);
  const [groupesMatieres, setGroupesMatieres] = useState<GroupeMatiereDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mousses, setMousses] = useState<any[]>([]);
  const [coefficientK, setCoefficientK] = useState<number>(1.30);
  const [coefficientKTemp, setCoefficientKTemp] = useState<string>('1.30');
  const [kCharge, setKCharge] = useState<boolean>(false);
  const [kError, setKError] = useState<string | null>(null);

  // Charger les mousses au montage du composant
  useEffect(() => {
    const loadMousses = async () => {
      try {
        const moussesData = await mousseService.getMousses();
        setMousses(moussesData);
      } catch (error) {
        console.error('Erreur lors du chargement des mousses:', error);
        setMousses([]);
      }
    };

    loadMousses();
  }, []);

  // 1. Réinitialiser kCharge quand le devisId change (nouvelle ouverture)
  useEffect(() => {
    setKCharge(false);
  }, [devisId]);

  // 2. Charger K depuis devisOptimise à l'ouverture
  useEffect(() => {
    // Essayer de charger K depuis la racine ou preferences_estimation
    const k = devisOptimise?.k || devisOptimise?.preferences_estimation?.k;
    
    if (k && !kCharge) {
      console.log(`🔵 [K] Chargement depuis devis: K=${k} (devisId: ${devisId})`);
      setCoefficientK(k);
      setCoefficientKTemp(k.toFixed(2));
      setKCharge(true);
    } else if (!k && !kCharge) {
      console.log(`🟡 [K] Valeur par défaut utilisée: K=1.30 (devisId: ${devisId})`);
      setKCharge(true);
    }
  }, [devisOptimise, kCharge, devisId]);

  // 3. Debounce pour mettre à jour coefficientK après modification de coefficientKTemp
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const value = parseFloat(coefficientKTemp);
      if (!isNaN(value) && value >= 1.10 && value <= 1.40) {
        setCoefficientK(value);
      }
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [coefficientKTemp]);

  // Fonction pour extraire les fournitures des articles
  const extraireFournitures = async (articles: Article[]): Promise<FournitureDetail[]> => {
    const fournitures: FournitureDetail[] = [];
    
    for (const article of articles) {
      const quantite = article.quantite || 1;
      const options = article.options || {};
      const dimensions = article.dimensions || {};
      
      // Mousses avec calcul adapté selon la forme
      if (options.mousse) {
        const longueur = dimensions.longueur || 0;
        const largeur = dimensions.largeur || 0;
        const hauteur = dimensions.hauteur || 0;
        const diametre = dimensions.diametre || 0;
        
        let volume = 0;
        let dimensionsText = '';
        
        // Calcul du volume selon la forme
        if (article.forme?.includes('cylindre')) {
          // Cylindre : π × r² × longueur
          if (diametre > 0 && longueur > 0) {
            const rayon = diametre / 2;
            volume = (Math.PI * rayon * rayon * longueur) / 1000000; // en m³
            dimensionsText = `Ø${diametre}×${longueur} cm`;
          }
        } else if (article.forme?.includes('rond')) {
          // Rond : π × r² × hauteur
          if (diametre > 0 && hauteur > 0) {
            const rayon = diametre / 2;
            volume = (Math.PI * rayon * rayon * hauteur) / 1000000; // en m³
            dimensionsText = `Ø${diametre}×${hauteur} cm`;
          }
        } else {
          // Rectangulaire : longueur × largeur × hauteur
          if (longueur > 0 && largeur > 0 && hauteur > 0) {
            volume = (longueur * largeur * hauteur) / 1000000; // en m³
            dimensionsText = `${longueur}×${largeur}×${hauteur} cm`;
          }
        }
        
        let typeMousse = '';
        let prixUnitaire = 1650; // Prix par défaut
        
        // Utiliser le nouveau système de mousses si disponible
        if (mousses.length > 0) {
          // Vérifier si c'est un ancien type générique ou un nouvel ID
          if (['souple', 'ferme', 'tres_ferme'].includes(options.mousse)) {
            // Ancien système : mapper vers les nouvelles mousses
            const typeMapping = {
              'souple': ['souple', 'standard', '30a', '35a'],
              'ferme': ['ferme', '40b', 'dunlopillo'],
              'tres_ferme': ['tres_ferme', 'haute_densite', 'premium']
            };
            
            const keywords = typeMapping[options.mousse as keyof typeof typeMapping] || [];
            const mousse = mousses.find(m => 
              keywords.some(keyword => m.nom.toLowerCase().includes(keyword))
            ) || mousses[0]; // Fallback sur la première mousse
            
            typeMousse = mousse.nom;
            prixUnitaire = mousse.prixM3;
          } else {
            // Nouveau système : rechercher par ID
            const mousse = mousses.find(m => m.id === options.mousse);
            if (mousse) {
              typeMousse = mousse.nom;
              prixUnitaire = mousse.prixM3;
            } else {
              // Fallback si ID non trouvé
              typeMousse = `Mousse ID ${options.mousse}`;
              prixUnitaire = 1650;
            }
          }
        } else {
          // Fallback sur l'ancien système si les mousses ne sont pas chargées
          switch (options.mousse) {
            case 'souple':
              typeMousse = 'Mousse souple';
              prixUnitaire = 120;
              break;
            case 'ferme':
              typeMousse = 'Mousse ferme';
              prixUnitaire = 150;
              break;
            case 'tres_ferme':
              typeMousse = 'Mousse très ferme';
              prixUnitaire = 180;
              break;
            default:
              typeMousse = `Mousse ${options.mousse}`;
              prixUnitaire = 1650;
              break;
          }
        }
        
        fournitures.push({
          article: article.nom || `Article ${article.id}`,
          type: typeMousse,
          dimensions: dimensionsText,
          volume: volume,
          quantite: volume * quantite,
          unite: 'm³',
          prixUnitaire: prixUnitaire
        });
      }
      
      // Fermetures adaptatives - Utilisation du nouveau service
      if (options.fermeture && (options.fermeture === 'glissiere' || options.fermeture === 'velcro')) {
        try {
          // Utiliser le nouveau service de fermetures adaptatives
          const fermetureAdaptative = await fermetureService.calculerFermetureAdaptative({
            forme: article.forme || 'rectangulaire_bandeau',
            dimensions: {
              longueur: dimensions.longueur,
              largeur: dimensions.largeur,
              diametre: dimensions.diametre,
              hauteur: dimensions.hauteur
            },
            typeFermeture: options.fermeture as 'glissiere' | 'velcro'
          });

          if (fermetureAdaptative) {
            const affichage = fermetureService.formaterAffichageFermeture(fermetureAdaptative);
            const alerte = fermetureService.verifierAlerteFermeture(fermetureAdaptative);
            
            fournitures.push({
              article: article.nom || `Article ${article.id}`,
              type: alerte.hasAlert ? `${affichage} ⚠️` : affichage,
              dimensions: `Calculée: ${fermetureAdaptative.longueurCalculee} cm`,
              quantite: quantite,
              unite: 'unités',
              prixUnitaire: fermetureAdaptative.prixUnitaire
            });
          } else {
            // Fallback sur l'ancien système
            throw new Error('Service fermetures indisponible');
          }
        } catch (error) {
          console.warn('Erreur service fermetures, utilisation fallback:', (error as Error).message);
          
          // Fallback sur l'ancien calcul avec visibilité claire
          let longueurFermeture = 0;
          if (article.forme?.includes('rectangulaire')) {
            longueurFermeture = Math.max(dimensions.longueur || 0, dimensions.largeur || 0);
          } else if (article.forme?.includes('rond')) {
            longueurFermeture = Math.round((dimensions.diametre || 0) * Math.PI / 2);
          } else {
            longueurFermeture = dimensions.longueur || 0;
          }
          
          if (longueurFermeture > 0) {
            const typeLabel = options.fermeture === 'glissiere' ? 'Glissière' : 'Velcro';
            fournitures.push({
              article: article.nom || `Article ${article.id}`,
              type: `${typeLabel} ${longueurFermeture} cm (FALLBACK)`,
              quantite: quantite,
              unite: 'unités',
              prixUnitaire: longueurFermeture * 0.15
            });
          }
        }
      }
      
      // Fournitures spécifiques aux bâches (forme rectangle_simple)
      if (article.forme === 'rectangle_simple') {
        // Œillets
        if (options.oeillets) {
          const espacement = options.espacementOeillets || 30;
          const perimetre = 2 * ((dimensions.longueur || 0) + (dimensions.largeur || 0));
          const nombreOeillets = Math.ceil(perimetre / espacement);
          
          fournitures.push({
            article: article.nom || `Article ${article.id}`,
            type: 'Œillets',
            quantite: nombreOeillets * quantite,
            unite: 'pcs',
            prixUnitaire: 0.50 // 50 centimes par œillet
          });
        }
        
        // Renforts aux angles
        if (options.renforts) {
          fournitures.push({
            article: article.nom || `Article ${article.id}`,
            type: 'Renforts aux angles',
            quantite: 4 * quantite, // 4 angles par bâche
            unite: 'pcs',
            prixUnitaire: 1.50 // 1.50€ par renfort
          });
        }
        
        // Sangles
        if (options.sangles) {
          const nombreSangles = options.nombreSangles || 2;
          const longueurSangle = (dimensions.largeur || 0) / 100; // en mètres
          
          fournitures.push({
            article: article.nom || `Article ${article.id}`,
            type: 'Sangles',
            quantite: (nombreSangles * longueurSangle) * quantite,
            unite: 'm',
            prixUnitaire: 6.00 // 6€ par mètre de sangle
          });
        }
        
        // Assemblage (si nécessaire)
        if ((options as any).assemblage) {
          fournitures.push({
            article: article.nom || `Article ${article.id}`,
            type: 'Assemblage',
            quantite: 1 * quantite,
            unite: 'forfait',
            prixUnitaire: 15.00 // 15€ forfait assemblage
          });
        }
      }
    }
    
    return fournitures;
  };

  // Charger les fournitures quand les articles ou mousses changent
  useEffect(() => {
    const loadFournitures = async () => {
      if (articles.length > 0) {
        try {
          const fournituresData = await extraireFournitures(articles);
          setFournitures(fournituresData);
        } catch (error) {
          console.error('Erreur lors du calcul des fournitures:', error);
          setFournitures([]);
        }
      } else {
        setFournitures([]);
      }
    };

    loadFournitures();
  }, [articles, mousses]);

  useEffect(() => {
    const fetchPieces = async () => {
      if (articles.length > 0 && matieres.length > 0) {
        setLoading(true);
        setError(null);
        
        try {
          const result = await apiService.genererDetailPieces(articles, matieres);
          
          if (result.success && result.pieces) {
            setPieces(result.pieces);
          } else {
            throw new Error(result.error || 'Erreur lors de la génération des pièces');
          }
        } catch (error: any) {
          console.error('❌ Erreur génération pièces:', error);
          setError(error.message || 'Erreur lors du chargement des pièces');
          setPieces([]);
        } finally {
          setLoading(false);
        }
      } else {
        setPieces([]);
      }
    };

    fetchPieces();
  }, [articles, matieres]);

  // Communiquer le nombre de pièces au composant parent
  useEffect(() => {
    if (onPiecesCalculated) {
      onPiecesCalculated(pieces.length);
    }
  }, [pieces.length, onPiecesCalculated]);

  // Extraire les groupes de matières depuis le calepinage OU estimer
  useEffect(() => {
    const loadGroupesMatieres = async () => {
      // Créer une clé unique basée sur les matières des articles
      const articlesMatieresKey = articles
        .map(a => {
          const materiaux = a.materiaux || {};
          return `${a.id}-${materiaux.dessus?.matiere}-${materiaux.dessus?.couleur}-${materiaux.dessous?.matiere}-${materiaux.dessous?.couleur}-${materiaux.cotes?.matiere}-${materiaux.cotes?.couleur}`;
        })
        .join('|');
      
      // Vérifier si devisOptimise contient des données valides
      let devisOptimiseValide = false;
      if (devisOptimise && optimisationActive && devisOptimise.groupesMatiere) {
        const matieresActuelles = new Set<string>();
        articles.forEach(article => {
          if (article.materiaux) {
            ['dessus', 'dessous', 'cotes'].forEach(position => {
              const materiau = article.materiaux[position as keyof typeof article.materiaux];
              if (materiau && materiau.matiere && materiau.couleur) {
                matieresActuelles.add(`${materiau.matiere}-${materiau.couleur}`);
              }
            });
            if (article.options?.grille && article.options?.grilleMatiere && article.options?.grilleCouleur) {
              matieresActuelles.add(`${article.options.grilleMatiere}-${article.options.grilleCouleur}`);
            }
          }
        });
        
        const matieresOptimisees = new Set(
          devisOptimise.groupesMatiere.map((g: any) => `${g.matiere}-${g.couleur}`)
        );
        
        devisOptimiseValide = Array.from(matieresActuelles).every(m => matieresOptimisees.has(m));
      }
      
      // MODE OPTIMISÉ : Utiliser les données du calepinage déjà effectué
      if (devisOptimiseValide && devisOptimise && optimisationActive && devisOptimise.groupesMatiere) {
        const groupes: GroupeMatiereDetail[] = devisOptimise.groupesMatiere.map((groupe: any) => {
          const matiereObj = matieres.find(m => m.id === groupe.matiere);
          return {
            matiere: groupe.matiere,
            matiereNom: matiereObj?.nom || groupe.matiere,
            couleur: groupe.couleur,
            laize: groupe.laize || 0,
            longueurTotale: groupe.calepinage?.longueurTotale || groupe.calepinage?.longueur_totale || 0,
            tauxChute: groupe.calepinage?.tauxChute || groupe.calepinage?.taux_chute,
            optimise: true
          };
        });
        setGroupesMatieres(groupes);
      } 
      // MODE ESTIMATION : Appeler l'API pour calculer les estimations
      else if (articles.length > 0 && matieres.length > 0 && kCharge) {
        // Vérifier si on doit recalculer ou charger depuis cache
        const kSauvegarde = devisOptimise?.k || devisOptimise?.preferences_estimation?.k;
        const hasGroupes = devisOptimise?.groupesMatiere && Array.isArray(devisOptimise.groupesMatiere) && devisOptimise.groupesMatiere.length > 0;
        const kInchange = kSauvegarde && Math.abs(coefficientK - kSauvegarde) < 0.001;
        
        // Si on a déjà des groupes ET K n'a pas changé : charger depuis cache
        if (hasGroupes && kInchange) {
          const groupes: GroupeMatiereDetail[] = devisOptimise.groupesMatiere.map((groupe: any) => {
            const matiereObj = matieres.find(m => m.id === groupe.matiere);
            
            // ✅ STRUCTURE DOUBLE: cache à la racine OU API imbriquée
            const longueur = groupe.longueurTotale ||                    // Cache (racine)
                           groupe.calepinage?.longueurTotale ||          // API fresh (imbriqué)
                           groupe.calepinage?.longueur_totale ||         // Fallback
                           0;
            
            return {
              matiere: groupe.matiere,
              matiereNom: matiereObj?.nom || groupe.matiere,
              couleur: groupe.couleur,
              laize: groupe.laize || 0,
              longueurTotale: longueur,
              tauxChute: groupe.tauxChute || groupe.calepinage?.taux_chute || groupe.calepinage?.tauxChute,
              optimise: false,
              estimationDetails: groupe.estimationDetails || groupe.calepinage?.estimation_details
            };
          });
          
          setGroupesMatieres(groupes);
          return;
        }
        
        try {
          // Appeler l'API d'estimation avec préférences (utiliser coefficientK)
          const result = await apiService.calculerEstimationsLongueurs({
            articles,
            matieres,
            preferences: {
              k: coefficientK,
              orientation_imposee: false,
              motifs: 'aucun',
              marge_securite_m: 0.15,
              granularite_m: 0.5
            }
          });
          
          if (result.success && result.groupes) {
            const groupes: GroupeMatiereDetail[] = result.groupes.map((groupe: any) => {
              const matiereObj = matieres.find(m => m.id === groupe.matiere);
              return {
                matiere: groupe.matiere,
                matiereNom: matiereObj?.nom || groupe.matiere,
                couleur: groupe.couleur,
                laize: groupe.laize || 0,
                longueurTotale: groupe.calepinage?.longueur_totale || groupe.calepinage?.longueurTotale || 0,
                tauxChute: groupe.calepinage?.taux_chute || groupe.calepinage?.tauxChute,
                optimise: false,
                estimationDetails: groupe.calepinage?.estimation_details
              };
            });
            setGroupesMatieres(groupes);
            
            // 4. Notifier le parent pour sauvegarder K et les groupes
            if (devisId && kCharge && onDevisOptimiseUpdate) {
              console.log(`🟢 [K] Notification parent pour sauvegarde: K=${coefficientK} (devisId: ${devisId})`);
              
              const devisOptimiseData = {
                ...(devisOptimise || {}),
                k: coefficientK,
                date_calcul: new Date().toISOString(),
                groupesMatiere: groupes
              };
              
              // Notifier le parent SANS déclencher de re-render ici
              setTimeout(() => {
                onDevisOptimiseUpdate(devisOptimiseData);
              }, 0);
            }
          } else {
            throw new Error('Erreur lors du calcul des estimations');
          }
        } catch (error) {
          console.error('❌ Erreur calcul estimations longueurs:', error);
          // Fallback : groupes de base sans longueur
          const groupesMap = new Map<string, GroupeMatiereDetail>();
          
          articles.forEach(article => {
            if (article.materiaux) {
              ['dessus', 'dessous', 'cotes'].forEach(position => {
                const materiau = article.materiaux[position as keyof typeof article.materiaux];
                if (materiau && materiau.matiere && materiau.couleur) {
                  const key = `${materiau.matiere}-${materiau.couleur}`;
                  const matiereObj = matieres.find(m => m.id === materiau.matiere);
                  
                  if (!groupesMap.has(key)) {
                    groupesMap.set(key, {
                      matiere: materiau.matiere,
                      matiereNom: matiereObj?.nom || materiau.matiere,
                      couleur: materiau.couleur,
                      laize: matiereObj?.laize || 140,
                      longueurTotale: 0,
                      optimise: false
                    });
                  }
                }
              });
            }
            
            if (article.options?.grille && article.options?.grilleMatiere && article.options?.grilleCouleur) {
              const key = `${article.options.grilleMatiere}-${article.options.grilleCouleur}`;
              const matiereObj = matieres.find(m => m.id === article.options.grilleMatiere);
              
              if (!groupesMap.has(key)) {
                groupesMap.set(key, {
                  matiere: article.options.grilleMatiere,
                  matiereNom: matiereObj?.nom || article.options.grilleMatiere,
                  couleur: article.options.grilleCouleur,
                  laize: matiereObj?.laize || 140,
                  longueurTotale: 0,
                  optimise: false
                });
              }
            }
          });
          
          setGroupesMatieres(Array.from(groupesMap.values()));
        }
      } else {
        setGroupesMatieres([]);
      }
    };

    loadGroupesMatieres();
  }, [devisOptimise, optimisationActive, articles, matieres, coefficientK, kCharge]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        <span className="ml-3 text-gray-600">Calcul des pièces en cours...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Erreur</h3>
            <div className="mt-2 text-sm text-red-700">
              {error}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (pieces.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        <p className="text-lg font-medium text-gray-900 mb-2">Aucune pièce à afficher</p>
        <p className="text-sm text-gray-500">
          Ajoutez des articles au devis pour voir le détail des pièces.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Section Longueurs de matière nécessaires */}
      {groupesMatieres.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <svg className="w-5 h-5 mr-2 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              Longueurs de matière nécessaires
              {optimisationActive && (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Optimisé
                </span>
              )}
            </h3>
            
            {/* Input pour modifier le coefficient K (seulement en mode estimation) */}
            {!optimisationActive && groupesMatieres.some(g => !g.optimise) && (
              <div className="flex flex-col items-end gap-1">
                <div className="flex items-center gap-2">
                  <label htmlFor="coefficientK" className="text-sm text-gray-700">
                    Coefficient d'atelier (k) :
                  </label>
                  <input
                    id="coefficientK"
                    type="number"
                    min="1.10"
                    max="1.40"
                    step="0.01"
                    value={coefficientKTemp}
                    onChange={(e) => {
                      setCoefficientKTemp(e.target.value);
                      setKError(null);
                    }}
                    onFocus={(e) => {
                      e.target.select();
                      setKError(null);
                    }}
                    onBlur={(e) => {
                      const value = parseFloat(e.target.value);
                      if (!isNaN(value) && value >= 1.10 && value <= 1.40) {
                        setCoefficientKTemp(value.toFixed(2));
                        setCoefficientK(value);
                        setKError(null);
                      } else if (!isNaN(value)) {
                        // Valeur hors limites
                        if (value < 1.10) {
                          setKError('⚠️ Minimum : 1.10');
                        } else if (value > 1.40) {
                          setKError('⚠️ Maximum : 1.40');
                        }
                        setCoefficientKTemp(coefficientK.toFixed(2));
                      } else {
                        setKError('⚠️ Valeur invalide');
                        setCoefficientKTemp(coefficientK.toFixed(2));
                      }
                    }}
                    className={`w-20 px-2 py-1 text-sm border rounded-md focus:ring-indigo-500 focus:border-indigo-500 ${
                      kError ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                  />
                  <span className="text-xs text-gray-500">(1.10 - 1.40)</span>
                </div>
                {kError && (
                  <span className="text-xs text-red-600 font-medium">{kError}</span>
                )}
              </div>
            )}
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-indigo-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Matière
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Couleur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Laize (cm)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Longueur nécessaire
                  </th>
                  {optimisationActive && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Taux de chute
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    État
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {groupesMatieres.map((groupe, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-indigo-25'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {groupe.matiereNom}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {groupe.couleur}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {groupe.laize} cm
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {groupe.longueurTotale > 0 ? (
                        <div>
                          <div className="font-bold text-indigo-900">
                            {groupe.longueurTotale.toFixed(0)} cm
                            <span className="ml-2 text-gray-500 font-normal">({(groupe.longueurTotale / 100).toFixed(2)} m)</span>
                          </div>
                          {/* Afficher les détails du calcul si estimation */}
                          {groupe.estimationDetails && !groupe.optimise && (
                            <div className="mt-2 text-xs text-gray-600 space-y-0.5">
                              <div>Surface/laize: {groupe.estimationDetails.longueur_area_cm.toFixed(0)} cm</div>
                              <div>× k={groupe.estimationDetails.k_applique.toFixed(2)} = {groupe.estimationDetails.longueur_estimee_cm.toFixed(0)} cm</div>
                              <div className="text-indigo-600 font-medium">→ Retenu: {groupe.estimationDetails.longueur_finale_cm.toFixed(0)} cm (arrondi + marge)</div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 italic">Non calculé</span>
                      )}
                    </td>
                    {optimisationActive && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {groupe.tauxChute !== undefined ? (
                          <span className={`font-medium ${
                            groupe.tauxChute < 0.15 ? 'text-green-600' : 
                            groupe.tauxChute < 0.25 ? 'text-yellow-600' : 
                            'text-red-600'
                          }`}>
                            {(groupe.tauxChute * 100).toFixed(1)}%
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {groupe.optimise ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Optimisé
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          📐 Estimation
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Résumé des longueurs */}
          <div className="mt-4 bg-indigo-50 px-6 py-3 border-t border-indigo-200">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">
                {groupesMatieres.length} matière{groupesMatieres.length > 1 ? 's' : ''} différente{groupesMatieres.length > 1 ? 's' : ''}
              </span>
              <span className="text-gray-600 font-medium">
                Longueur totale commande : {groupesMatieres.reduce((total, g) => total + g.longueurTotale, 0).toFixed(0)} cm 
                ({(groupesMatieres.reduce((total, g) => total + g.longueurTotale, 0) / 100).toFixed(2)} m)
              </span>
            </div>
            {optimisationActive && (
              <div className="mt-2 text-xs text-gray-500 italic">
                💡 Les longueurs affichées tiennent compte de l'optimisation du calepinage pour minimiser les chutes
              </div>
            )}
          </div>
        </div>
      )}

      {/* Section Détail des pièces */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          Détail des pièces à découper
        </h3>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Article
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Pièce
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Dimensions (cm)
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Surface (m²)
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Matière
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Couleur
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {pieces.map((piece, index) => (
            <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {piece.article}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {piece.piece}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {piece.dimensions}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {piece.surface.toFixed(3)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {piece.matiere}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {piece.couleur}
                </span>
              </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {/* Résumé simple */}
        <div className="mt-4 bg-gray-50 px-6 py-3 border-t border-gray-200">
        <div className="flex justify-between items-center text-sm">
          <span className="text-gray-600">
            Total : {pieces.length} pièce{pieces.length > 1 ? 's' : ''}
          </span>
          <span className="text-gray-600">
            Surface totale : {pieces.reduce((total, piece) => total + piece.surface, 0).toFixed(3)} m²
          </span>
          </div>
        </div>
      </div>
    </div>

      {/* Section Fournitures nécessaires */}
      {fournitures.length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <svg className="w-5 h-5 mr-2 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            Fournitures nécessaires
          </h3>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-orange-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Article
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fourniture
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dimensions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Volume
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantité
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Prix unit.
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {fournitures.map((fourniture, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-orange-25'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {fourniture.article}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                        {fourniture.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {fourniture.dimensions || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {fourniture.volume ? `${fourniture.volume.toFixed(3)} m³` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className="font-medium">
                        {fourniture.quantite.toFixed(3)} {fourniture.unite}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {fourniture.prixUnitaire.toFixed(2)} €/{fourniture.unite}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {(fourniture.quantite * fourniture.prixUnitaire).toFixed(2)} €
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Résumé des fournitures */}
          <div className="mt-4 bg-orange-50 px-6 py-3 border-t border-orange-200">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">
                Total fournitures : {fournitures.length} type{fournitures.length > 1 ? 's' : ''}
              </span>
              <span className="text-gray-600 font-medium">
                Coût total : {fournitures.reduce((total, f) => total + (f.quantite * f.prixUnitaire), 0).toFixed(2)} €
              </span>
            </div>
          </div>

          {/* Légende pour les fermetures */}
          {fournitures.some(f => f.type.includes('⚠️')) && (
            <div className="mt-3 bg-blue-50 border border-blue-200 rounded-md p-3">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-blue-800">Fermetures adaptatives - Sélection automatique</h4>
                  <div className="mt-1 text-sm text-blue-700">
                    <p className="mb-2"><span className="font-medium">Méthode de calcul :</span></p>
                    <ol className="list-decimal list-inside space-y-1 mb-3">
                      <li>Calcul de la longueur selon la forme (rectangulaire: max L/l, rond: demi-circonférence, bâche: largeur)</li>
                      <li>Recherche dans votre stock : taille exacte → plus petite supérieure → plus grande disponible</li>
                      <li>Calcul du temps de montage majoré selon la longueur (+20% à +60%)</li>
                    </ol>
                    <p><span className="font-medium">⚠️ Alertes :</span></p>
                    <ul className="mt-1 list-disc list-inside space-y-1">
                      <li>Taille exacte : Vérifiez les mesures pour éviter une fermeture trop juste</li>
                      <li>Écart important (&gt;20cm) : Fermeture plus grande que nécessaire</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DetailPieces;
